import React, { useEffect, useMemo } from "react";
import { Modal, Slide, Stack, Typography, Box, IconButton } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as GlobalIcon } from "../../../../../../../vendor/_new/icon/services/global.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";

import colors from "../../../../../../../utils/colors";
import { GreenSwitch } from "../../../../../../common/Greens";
import { SaveButton, CancelButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import useAuth from "../../../../../../../hooks/useAuth";
import { UserType } from "../../../../../../../utils/constants";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateWafSettingsForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

/** True when any WAF engine is explicitly on (strict: avoids truthy strings like "false"). */
export const isWafModuleOn = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1";
  }
  return false;
};

const isWafEngineEnabled = (wafConfig) => {
  if (!wafConfig || typeof wafConfig !== "object") return false;
  const smartParse =
    wafConfig.smart_parse_module_active ?? wafConfig.smartParseModuleActive;
  const sdSig = wafConfig.sd_sig_module_active ?? wafConfig.sdSigModuleActive;
  return isWafModuleOn(smartParse) || isWafModuleOn(sdSig);
};

/** When Global WAF toggles: off → disable enabled engines; on → enable Smart Parse. */
export function resolveGlobalWafEngineCascade({
  active,
  wasActive,
  smartParseActive,
  sdSigActive,
}) {
  let nextSmartParse = smartParseActive;
  let nextSdSig = sdSigActive;

  if (wasActive === active) {
    return { nextSmartParse, nextSdSig };
  }

  if (!active) {
    if (isWafModuleOn(smartParseActive)) nextSmartParse = false;
    if (isWafModuleOn(sdSigActive)) nextSdSig = false;
  } else {
    nextSmartParse = true;
  }

  return { nextSmartParse, nextSdSig };
};

const ConfigGlobalWAFSettingsModal = ({ siteUid, open, handleClose }) => {
  const { userType } = useAuth();
  const { wafConfig, configWafSetting, getWAFConfig } = useWAFConfig();
  const { showSuccess, showError } = useNotification();
  const [active, set_active] = React.useState(false);
  const [use_cache, set_use_cache] = React.useState(false);
  const [request_payload, set_request_payload] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [baselineKey, setBaselineKey] = React.useState(0);

  const formValidation = useMemo(() => validateWafSettingsForm({ variant: "global" }), []);

  useEffect(() => {
    if (open && siteUid) {
      getWAFConfig(siteUid);
    }
  }, [open, siteUid, getWAFConfig]);

  const atLeastOneWafEngineEnabled = isWafEngineEnabled(wafConfig);
  const globalWafSwitchDisabled = saving;
  const secondarySettingsDisabled = saving || !active;

  useEffect(() => {
    if (wafConfig) {
      set_active(wafConfig?.active);
      set_request_payload(wafConfig?.audit_req_body);
      set_use_cache(wafConfig?.use_cache);
      if (open) setBaselineKey((k) => k + 1);
    }
  }, [wafConfig, open]);

  const formSnapshot = useMemo(
    () => ({ active, use_cache, request_payload }),
    [active, use_cache, request_payload],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: saving });

  const handleSave = async () => {
    const validation = validateWafSettingsForm({ variant: "global" });
    if (!validation.valid) return;

    setSaving(true);
    if (userType !== UserType.ORG_USER) {
      try {
        const tasks = [];
        const activeChanged = wafConfig?.active !== active;

        if (activeChanged) {
          const { nextSmartParse, nextSdSig } = resolveGlobalWafEngineCascade({
            active,
            wasActive: wafConfig?.active,
            smartParseActive: wafConfig?.smart_parse_module_active,
            sdSigActive: wafConfig?.sd_sig_module_active,
          });
          const cascadePayload = { active };
          if (wafConfig?.smart_parse_module_active !== nextSmartParse) {
            cascadePayload.smart_parse_module_active = nextSmartParse;
          }
          if (wafConfig?.sd_sig_module_active !== nextSdSig) {
            cascadePayload.sd_sig_module_active = nextSdSig;
          }
          tasks.push(() => configWafSetting(siteUid, "globalWafCascadeChange", cascadePayload));
        }
        if (wafConfig?.use_cache !== use_cache) {
          tasks.push(() => configWafSetting(siteUid, "useCacheChange", { use_cache: use_cache }));
        }
        if (wafConfig?.audit_req_body !== request_payload) {
          tasks.push(() => configWafSetting(siteUid, "requestPayloadChange", { req_body_enabled: request_payload }));
        }
        if (tasks.length > 0) {
          for (const task of tasks) {
            await task();
          }
          await getWAFConfig(siteUid);
          showSuccess("Global WAF settings saved successfully");
        } else {
          showSuccess("No changes to save");
        }
      } catch (err) {
        console.error(err);
        showError("Failed to save Global WAF settings");
      }
    }
    setSaving(false);
    handleClose();
  };

  return (
    <>
    <Modal 
      open={open} 
      onClose={requestClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GlobalIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    Global WAF settings
                  </Typography>
                  <FormHelpTooltip tooltip="Site-wide WAF master switch, request body audit logging, and edge caching. These apply across all WAF engines on this domain." />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Configure firewall settings that apply across all WAF profiles.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  {!atLeastOneWafEngineEnabled && !active && (
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        backgroundColor: colors.neutral[50],
                        border: `1px solid ${colors.neutral[200]}`,
                      }}
                    >
                      <InfoIcon style={{ color: colors.neutral[600], width: 20, height: 20, flexShrink: 0, marginTop: 2 }} />
                      <Typography sx={ { fontSize: 13, lineHeight: "20px", color: colors.neutral[700] }}>
                        Turning on Global WAF will enable Layerd AI Smart Parse by default.
                      </Typography>
                    </Stack>
                  )}
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={3}
                    justifyContent={"space-between"}
                    sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, backgroundColor: "white", p: 3 }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      Global WAF
                    </Typography>
                    <Box sx={{ pointerEvents: globalWafSwitchDisabled ? "none" : "auto", display: "inline-flex" }}>
                      <GreenSwitch
                        disabled={globalWafSwitchDisabled}
                        checked={active}
                        onChange={(e) => {
                          if (globalWafSwitchDisabled) return;
                          set_active(e.target.checked);
                        }}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={3}
                    justifyContent={"space-between"}
                    sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, backgroundColor: "white", p: 3 }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      Payload Logging
                    </Typography>
                    <Box sx={{ pointerEvents: secondarySettingsDisabled ? "none" : "auto", display: "inline-flex" }}>
                      <GreenSwitch
                        disabled={secondarySettingsDisabled}
                        checked={request_payload}
                        onChange={(e) => {
                          if (secondarySettingsDisabled) return;
                          set_request_payload(e.target.checked);
                        }}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={3}
                    justifyContent={"space-between"}
                    sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, backgroundColor: "white", p: 3 }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      Use Cache
                    </Typography>
                    <Box sx={{ pointerEvents: secondarySettingsDisabled ? "none" : "auto", display: "inline-flex" }}>
                      <GreenSwitch
                        disabled={secondarySettingsDisabled}
                        checked={use_cache}
                        onChange={(e) => {
                          if (secondarySettingsDisabled) return;
                          set_use_cache(e.target.checked);
                        }}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    direction={"row"}
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary["alpha"],
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <SaveButton onClick={handleSave} disabled={saving} />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};
export default ConfigGlobalWAFSettingsModal;
